import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LangProvider } from './contexts/LangContext';
import GapAssessmentLandingPage from './components/GapAssessmentLandingPage';
import CookieBanner from './components/CookieBanner';
import ResetPasswordScreen from './components/ResetPasswordScreen';
import NotFoundPage from './components/NotFoundPage';
import PrerenderReady from './components/PrerenderReady';

// Panel wewnetrzny (/app, za logowaniem) - lazy-loaded osobnym chunkiem, zeby odwiedzajacy strony
// publiczne (landing, normy, blog...) nie sciagali GeneratorPanel/AdminPanel/HLSPanel i reszty.
// Patrz AppShell.jsx.
const AppInner = lazy(() => import('./components/AppShell'));

// Faza 4 (24.07): 7 publicznych stron dzieli kod JS per-strona (React.lazy) zeby zmniejszyc
// "unused JavaScript" z PageSpeed. Pierwsza proba (ta sama data) zostala WYCOFANA po realnej
// weryfikacji w przegladarce - React.lazy() ZAWSZE zawiesza sie na pierwszym renderze (mikrotask
// w lazyInitializer, niezaleznie od cache), wiec hydrateRoot na realnej wizycie widzial Suspense
// fallback zamiast tresci upieczonej przez react-snap -> mismatch -> blad #418/#423, flash +
// przerenderowanie po stronie klienta. Naprawione (ten kod): main.jsx PRZED hydrateRoot preloaduje
// (patrz routePreload.js) chunk dla trasy na ktorej user faktycznie ladowal strone i podaje go tu
// jako `preloadedRoute` - dla TEJ trasy renderRoute() zwraca gotowy komponent bez Suspense (pierwszy
// render identyczny z HTML-em), dla WSZYSTKICH innych (nawigacja kliencka po zaladowaniu) zwykly
// lazy()+Suspense dziala normalnie i bezpiecznie (nie ma tam zadnego server HTML do pogodzenia).
const AiSeoPage = lazy(() => import('./components/AiSeoPage'));
const PartnerPage = lazy(() => import('./components/PartnerPage'));
const GapAuditLandingPage = lazy(() => import('./components/GapAuditLandingPage'));
const PowerModulesLandingPage = lazy(() => import('./components/PowerModulesLandingPage'));
const HLSBuilderLandingPage = lazy(() => import('./components/HLSBuilderLandingPage'));
const AqapLandingPage = lazy(() => import('./components/AqapLandingPage'));
const Aqap2131LandingPage = lazy(() => import('./components/Aqap2131LandingPage'));
const GdpLandingPage = lazy(() => import('./components/GdpLandingPage'));
const DemoPage = lazy(() => import('./components/DemoPage'));
const PakietyPage = lazy(() => import('./components/PakietyPage'));
const BlogPage = lazy(() => import('./components/BlogPage'));
const NormPage = lazy(() => import('./components/NormPage'));
// 25.07: PrivacyPage/TermsPage byly eager bez konkretnego powodu (w odroznieniu od LandingPage,
// ktore MUSI byc eager - patrz komentarz wyzej) - kazdy odwiedzajacy KTORAKOLWIEK strone sciagal
// ich kod niepotrzebnie (PageSpeed mobile flagowal to jako "unused JavaScript"). Przeniesione do
// tego samego lazy+preload mechanizmu co reszta - wymaga wpisu w routePreload.js ROUTE_CHUNKS,
// inaczej wraca dokladnie ten sam blad hydratacji #418/#423 opisany wyzej.
const PrivacyPage = lazy(() => import('./components/PrivacyPage'));
const TermsPage = lazy(() => import('./components/TermsPage'));
// Gap Assessment by MQS (10.08): blog i cennik wlasne dla produktu, zatwierdzony koncept z
// artefaktu przegladu 10.08 - patrz components/gap/BlogPage.jsx i CennikPage.jsx. Ten sam wzorzec
// lazy+preload co PrivacyPage/TermsPage powyzej (klucze GapBlogPage/GapCennikPage w routePreload.js).
const GapBlogPage = lazy(() => import('./components/gap/BlogPage'));
const GapCennikPage = lazy(() => import('./components/gap/CennikPage'));
const GapSampleReportPage = lazy(() => import('./components/gap/SampleReportPage'));
// Gap Assessment by MQS - Faza 0.2 (05.08): trasa deweloperska /app, calkowicie odrebna od reszty
// ISO Forge (wlasny stan/auth, patrz GapMvp.jsx) - swiadomie POZA mechanizmem renderRoute/preload
// powyzej (ten mechanizm istnieje dla SEO/hydratacji publicznych stron produkcyjnych ISO Forge,
// niepotrzebny dla trasy narzedziowej dostepnej tylko lokalnie).
const GapMvpPage = lazy(() => import('./components/GapMvp'));

function PublicPageLoadingFallback() {
  return <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }} />;
}

// Bez tego, awaria ladowania lazy-chunka (np. przerwane polaczenie, stary cache wskazujacy na
// usuniety hash pliku) ginie po cichu wewnatrz Suspense - strona zostaje wizualnie "pusta" albo
// "zamrozona" bez zadnego komunikatu w UI, tylko z bledem w konsoli ktorego user nie zobaczy.
class RouteErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('RouteErrorBoundary caught:', error, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, background: 'var(--bg-page)', color: 'var(--text-primary)', padding: 24, textAlign: 'center' }}>
          <p style={{ fontSize: '0.95rem', fontWeight: 700 }}>Nie udalo sie zaladowac strony.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>Odśwież</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// key musi sie zgadzac z routePreload.js (ROUTE_CHUNKS[].key). Gdy preloadedRoute pasuje po key,
// uzywamy JEGO props (obliczonych w routePreload.js dla konkretnej trasy na ktorej user wyladowal,
// np. wlasciwego normId) - nie tych przekazanych do renderRoute, ktore sluza tylko sciezce lazy.
function renderRoute(key, LazyComponent, lazyProps, preloadedRoute, Fallback = PublicPageLoadingFallback) {
  if (preloadedRoute && preloadedRoute.key === key && preloadedRoute.Component) {
    const Preloaded = preloadedRoute.Component;
    return <RouteErrorBoundary><Preloaded {...preloadedRoute.props} /></RouteErrorBoundary>;
  }
  return <RouteErrorBoundary><Suspense fallback={<Fallback />}><LazyComponent {...lazyProps} /></Suspense></RouteErrorBoundary>;
}

function AppLoadingFallback() {
  // /app nie jest w reactSnap.include (swiadomie nie indeksowane, chronione logowaniem) - marker
  // pozwala globalnemu "waitFor" (patrz package.json) zakonczyc oczekiwanie na TYM stanie zamiast
  // liczyc na to ze AppShell kiedykolwiek go zastapi podczas crawla (nigdy sie nie zaloguje).
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950">
      <div className="text-slate-400">Ladowanie...</div>
      <PrerenderReady />
    </div>
  );
}

function ActivateScreen() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { activateAccount } = useAuth();
  const [state, setState] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    activateAccount(token)
      .then(() => { setState('ok'); setTimeout(() => navigate('/app'), 2000); })
      .catch(e => { setState('error'); setError(e.message); });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="text-center max-w-sm">
        {state === 'loading' && <><div className="text-4xl mb-4 animate-pulse">⏳</div><p className="text-slate-300">Aktywacja konta...</p></>}
        {state === 'ok' && <><div className="text-5xl mb-4">✅</div><h2 className="text-xl font-bold text-white mb-2">Konto aktywowane!</h2><p className="text-slate-400">Za chwilę zostaniesz przekierowany do aplikacji.</p></>}
        {state === 'error' && <><div className="text-5xl mb-4">❌</div><h2 className="text-xl font-bold text-white mb-2">Błąd aktywacji</h2><p className="text-slate-400 mb-4">{error}</p><button onClick={() => navigate('/app')} className="text-blue-400 hover:text-blue-300 text-sm underline">Przejdź do logowania</button></>}
      </div>
    </div>
  );
}

function AccentInit() {
  useEffect(() => {
    const saved = localStorage.getItem('iso_accent') || 'blue';
    if (saved !== 'blue') document.documentElement.setAttribute('data-accent', saved);
    else document.documentElement.removeAttribute('data-accent');
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Panel "Ruch na zywo" w adminie (29.07/30.07) - beacon niezalezny od GA/GTM, wlasna baza. Odpala
// sie przy kazdej zmianie adresu (ten sam choke-point co ScrollToTop). document.referrer NIE
// zmienia sie przy nawigacji SPA (tylko przy prawdziwym zaladowaniu strony przez przegladarke) -
// wysylany wiec TYLKO przy pierwszym wywolaniu w tej sesji karty, zeby nie powielac tego samego
// "skad przyszedl" na kazda kolejna podstrone. Admin (Ty) pomijany, zeby wlasne testowanie nie
// zasmiecalo danych porownywanych z Analytics.
let trafficBeaconReferrerSent = false;
// 30.07: identyfikator sesji TYLKO na czas otwartej karty (sessionStorage, nie cookie/localStorage) -
// pozwala grupowac wejscia tej samej wizyty w panelu admina, bez sledzenia powracajacych odwiedzajacych
// (nowa karta/nastepny dzien = nowa sesja). Zgodne z tym co juz powiedziano Wiktorii: zero trwalego
// zapisu w przegladarce odwiedzajacego.
function getTrafficSessionId() {
  let id = sessionStorage.getItem('iso_traffic_session');
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem('iso_traffic_session', id);
  }
  return id;
}
// Gap Assessment by MQS (10.08) - odczytuje role z WLASNEGO, niezaleznego tokena Gap Assessment
// (localStorage 'gap_mvp_token', patrz gap-mvp/api.js TOKEN_KEY) - ten sam JWT co reszta apki
// Gap Assessment podpisuje/czyta, ale TrafficBeacon (komponent globalny, POZA drzewem GapMvp) nie ma
// dostepu do jego react-kontekstu. Dekodowanie payloadu bez weryfikacji podpisu jest tu wystarczajace
// i bezpieczne - to tylko heurystyka wykluczenia z analityki (nie kontrola dostepu), a JWT samo w
// sobie nie jest tajne (przegladarka i tak je ma w localStorage).
function getGapMvpRole() {
  try {
    const token = localStorage.getItem('gap_mvp_token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.role || null;
  } catch { return null; }
}

function TrafficBeacon() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (typeof navigator !== 'undefined' && /ReactSnap/.test(navigator.userAgent)) return;
    // Przy pelnym przeladowaniu strony (nie samej nawigacji SPA) `loading` startuje true dopoki
    // /api/auth/me sie nie rozstrzygnie (patrz AuthContext.jsx) - bez tej straznicy admin dostawal
    // jeden pasozytniczy wpis przy kazdym twardym odswiezeniu, zanim user.role bylo jeszcze znane.
    // Dla anonimowych odwiedzajacych (bez tokena) loading jest false od pierwszego renderu, wiec
    // to nie dodaje zadnego opoznienia dla ruchu ktory nas realnie interesuje.
    if (loading) return;
    if (user?.role === 'admin') return;
    // Bledny na Gap Assessment: `useAuth()` wyzej to STARY kontekst logowania ISO Forge, ktorego
    // /app/* (GapMvp.jsx) w ogole nie uzywa (wlasny token) - powyzszy check nigdy nie wylapywal
    // admina Gap Assessment. Znalezione 10.08 po tym jak wlasne testowanie panelu admina zafalszowalo
    // statystyki ruchu. Sprawdzane tylko na /app (tam gdzie w ogole moze byc zalogowany user Gap Assessment).
    if (pathname.startsWith('/app') && getGapMvpRole() === 'admin') return;

    const params = new URLSearchParams(window.location.search);
    const isExternalReferrer = document.referrer && !document.referrer.startsWith(window.location.origin);
    const payload = {
      path: pathname,
      referrer: (!trafficBeaconReferrerSent && isExternalReferrer) ? document.referrer : null,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      session_id: getTrafficSessionId(),
    };
    trafficBeaconReferrerSent = true;

    fetch('/api/ping/hit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, [pathname, user, loading]);

  return null;
}

// Siatka bezpieczenstwa dla "waitFor" (package.json) - kazda z 10 publicznych stron ustawia
// data-prerender-ready sama, natychmiast po zamontowaniu prawdziwej tresci (patrz PrerenderReady.jsx).
// /app nie ma i celowo nie ma dostawac takiego markera (chronione logowaniem, nigdy nie indeksowane) -
// ale react-snap moze je i tak odkryc crawlujac linki z innych stron. Ten timer to wylacznie backstop
// na wypadek route bez wlasnego markera - jesli nic go nie ustawilo w rozsadnym czasie, ustawia go sam,
// zeby crawl nie czekal pelne 30s zamiast realnie krotszego, kontrolowanego czasu.
function PrerenderReadyFallback() {
  useEffect(() => {
    const id = setTimeout(() => {
      if (!document.body.hasAttribute('data-prerender-ready')) {
        document.body.setAttribute('data-prerender-ready', 'true');
      }
    }, 4000);
    return () => clearTimeout(id);
  }, []);
  return null;
}

export default function App({ preloadedRoute }) {
  // Gap Assessment by MQS (06.08) - ten sandbox (port 5190) jest wylacznie dla tego produktu (patrz
  // przekierowanie "/" -> "/app" wyzej) - banery/zgody cookies ISO Forge to szum nienalezacy do
  // innego produktu, ukryte na calej galezi /app.
  const { pathname } = useLocation();
  const isGapAssessment = pathname.startsWith('/app');
  return (
    <ThemeProvider>
    <LangProvider>
    <AuthProvider>
      <PrerenderReadyFallback />
      <ScrollToTop />
      <TrafficBeacon />
      <AccentInit />
      <Routes>
        {/* Gap Assessment by MQS (09.08) - ten sandbox na dedykowanym porcie 5190 sluzy WYLACZNIE
            temu produktowi; strona glowna ISO Forge nigdy nie powinna sie pokazac tutaj. Wczesniej
            "/" przekierowywalo prosto do /app (logowanie) - teraz jest tu wlasny landing page
            produktu (zatwierdzony artefakt, patrz GapAssessmentLandingPage.jsx), eager import tak
            jak poprzedni LandingPage (jedyna trasa celowo NIE lazy - pierwszy render bez Suspense). */}
        <Route path="/" element={<GapAssessmentLandingPage />} />
        {/* 10.08: blog i cennik wlasne dla Gap Assessment (nie odziedziczony content ISO Forge z
            bloku ponizej) - zatwierdzony koncept z artefaktu przegladu, patrz components/gap/. */}
        <Route path="/blog" element={renderRoute('GapBlogPage', GapBlogPage, {}, preloadedRoute)} />
        <Route path="/blog/:slug" element={renderRoute('GapBlogPage', GapBlogPage, {}, preloadedRoute)} />
        <Route path="/cennik" element={renderRoute('GapCennikPage', GapCennikPage, {}, preloadedRoute)} />
        <Route path="/przyklad-raportu" element={renderRoute('GapSampleReportPage', GapSampleReportPage, {}, preloadedRoute)} />
        {/* 09.08, decyzja Marcina przy pierwszym publicznym wdrozeniu gapassessment.pl: caly ponizszy
            blok tras (blog, generatory norm, /demo z wideo ISO Forge, /pakiety, /partner, /gap-audit,
            /power-modules, /hls-builder, stary panel /app) to odziedziczony z forka content
            marketingowy ISO Forge - opisuje NIEISTNIEJACY w Gap Assessment produkt (generator
            dokumentow od zera) i pokazuje cudza marke (wideo demo ISO Forge). Na start tylko landing
            (/) + realna apka (/app/*), dopoki nie powstanie wlasny content pod Gap Assessment. Kod tras
            zostaje (nieusuwane, lazy - nie obciaza bundla) do ewentualnego przywrocenia pozniej.
            UWAGA (10.08): /app jest OD TEJ SESJI zajete przez prawdziwa, zywa apke Gap Assessment
            (patrz Route "/app/wildcard" z GapMvpPage POZA tym zakomentowanym blokiem) - odziedziczony
            stary panel ISO Forge ponizej (AppInner, AppShell.jsx) NIE MOZE zostac przywrocony pod ta
            sama sciezke bez konfliktu, wymagalby zmiany na inna trase.
        <Route path="/blog" element={renderRoute('BlogPage', BlogPage, {}, preloadedRoute)} />
        <Route path="/blog/:slug" element={renderRoute('BlogPage', BlogPage, {}, preloadedRoute)} />
        <Route path="/generator-iso-9001" element={renderRoute('NormPage', NormPage, { normId: '9001' }, preloadedRoute)} />
        <Route path="/generator-dokumentow-iso-9001" element={<Navigate to="/generator-iso-9001" replace />} />
        <Route path="/generator-dokumentow-iso-14001" element={<Navigate to="/generator-iso-14001" replace />} />
        <Route path="/generator-dokumentow-iso-27001" element={<Navigate to="/generator-iso-27001" replace />} />
        <Route path="/generator-dokumentow-nis2" element={<Navigate to="/generator-nis2" replace />} />
        <Route path="/generator-iso-14001" element={renderRoute('NormPage', NormPage, { normId: '14001' }, preloadedRoute)} />
        <Route path="/generator-iso-14001-2026" element={renderRoute('NormPage', NormPage, { normId: '14001-2026' }, preloadedRoute)} />
        <Route path="/generator-iso-45001" element={renderRoute('NormPage', NormPage, { normId: '45001' }, preloadedRoute)} />
        <Route path="/generator-iso-27001" element={renderRoute('NormPage', NormPage, { normId: '27001' }, preloadedRoute)} />
        <Route path="/generator-iso-22301" element={renderRoute('NormPage', NormPage, { normId: '22301' }, preloadedRoute)} />
        <Route path="/generator-iatf-16949" element={renderRoute('NormPage', NormPage, { normId: '16949' }, preloadedRoute)} />
        <Route path="/generator-vda-63" element={renderRoute('NormPage', NormPage, { normId: 'vda63' }, preloadedRoute)} />
        <Route path="/generator-nis2" element={renderRoute('NormPage', NormPage, { normId: 'nis2' }, preloadedRoute)} />
        <Route path="/generator-iso-42001" element={renderRoute('NormPage', NormPage, { normId: '42001' }, preloadedRoute)} />
        <Route path="/generator-aqap-2110" element={renderRoute('NormPage', NormPage, { normId: 'aqap2110' }, preloadedRoute)} />
        <Route path="/generator-aqap-2210" element={renderRoute('NormPage', NormPage, { normId: 'aqap2210' }, preloadedRoute)} />
        <Route path="/generator-aqap-2131" element={renderRoute('NormPage', NormPage, { normId: 'aqap2131' }, preloadedRoute)} />
        <Route path="/generator-gdp" element={renderRoute('NormPage', NormPage, { normId: 'gdp' }, preloadedRoute)} />
        <Route path="/aqap-2110" element={renderRoute('AqapLandingPage', AqapLandingPage, {}, preloadedRoute)} />
        <Route path="/aqap-2131" element={renderRoute('Aqap2131LandingPage', Aqap2131LandingPage, {}, preloadedRoute)} />
        <Route path="/gdp" element={renderRoute('GdpLandingPage', GdpLandingPage, {}, preloadedRoute)} />
        <Route path="/gap-audit" element={renderRoute('GapAuditLandingPage', GapAuditLandingPage, {}, preloadedRoute)} />
        <Route path="/power-modules" element={renderRoute('PowerModulesLandingPage', PowerModulesLandingPage, {}, preloadedRoute)} />
        <Route path="/hls-builder" element={renderRoute('HLSBuilderLandingPage', HLSBuilderLandingPage, {}, preloadedRoute)} />
        <Route path="/app/activate/:token" element={<ActivateScreen />} />
        <Route path="/app" element={renderRoute('AppShell', AppInner, {}, preloadedRoute, AppLoadingFallback)} />
        <Route path="/app/*" element={renderRoute('AppShell', AppInner, {}, preloadedRoute, AppLoadingFallback)} />
        <Route path="/demo" element={renderRoute('DemoPage', DemoPage, {}, preloadedRoute)} />
        <Route path="/pakiety" element={renderRoute('PakietyPage', PakietyPage, {}, preloadedRoute)} />
        <Route path="/ai-seo" element={renderRoute('AiSeoPage', AiSeoPage, {}, preloadedRoute)} />
        <Route path="/partner" element={renderRoute('PartnerPage', PartnerPage, {}, preloadedRoute)} />
        */}
        {/* 09.08: polityka prywatnosci i regulamin WYJATKOWO przywrocone na wyrazna prosbe Marcina
            ("dodaj jako ostatni krok baner cookie, polityke prywatnosci i regulamin") - obie strony
            przepisane pod Gap Assessment (patrz PrivacyPage.jsx/TermsPage.jsx), nie ISO Forge. */}
        <Route path="/polityka-prywatnosci" element={renderRoute('PrivacyPage', PrivacyPage, {}, preloadedRoute)} />
        <Route path="/regulamin" element={renderRoute('TermsPage', TermsPage, {}, preloadedRoute)} />
        <Route path="/reset-password" element={<ResetPasswordScreen />} />
        <Route path="/app/*" element={<Suspense fallback={<PublicPageLoadingFallback />}><GapMvpPage /></Suspense>} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {/* 09.08: baner ponownie wlaczony (na prosbe Marcina) - CookieBanner.jsx przepisany na
          uczciwa, informacyjna wersje (Serwis nie uzywa zadnych cookies, patrz komentarz w tym
          pliku) i linkuje do dzialajacej juz /polityka-prywatnosci. Ukryty na /app (apka wlasna,
          zaloguj sie i tak wymaga akceptacji), pokazany na landing page i stronach prawnych. */}
      {!isGapAssessment && <CookieBanner />}
    </AuthProvider>
    </LangProvider>
    </ThemeProvider>
  );
}
